import { useCallback, useMemo, useState } from 'react'

import type {
  ForecastMarketPair,
  PriceQuantile,
  PricingMode,
  PricingStrategyConfig,
} from '@/features/bidding/types/forecastPricing'
import { QUANTILE_ORDER } from '@/features/bidding/types/forecastPricing'

function createInitialConfig(levelIds: string[]): PricingStrategyConfig {
  return {
    levels: levelIds.map((levelId, index) => ({
      levelId,
      mode: 'fixed' as PricingMode,
      market: 'mfrr' as ForecastMarketPair,
      // Default quantile escalates with level: p10, p25, mean for levels 1, 2, 3
      quantile: QUANTILE_ORDER[Math.min(index, QUANTILE_ORDER.length - 1)],
    })),
  }
}

/**
 * Get the minimum allowed quantile for a level based on preceding levels.
 * Only applies constraint when the previous level uses the SAME market.
 * Returns 'p10' if first level, all previous are fixed, or previous levels use different markets.
 */
export function getMinQuantileForLevel(config: PricingStrategyConfig, levelIndex: number): PriceQuantile {
  if (levelIndex === 0) return 'p10'

  const currentMarket = config.levels[levelIndex].market

  // Find the closest preceding level that uses forecast mode with the SAME market
  for (let i = levelIndex - 1; i >= 0; i--) {
    if (config.levels[i].mode === 'forecast' && config.levels[i].market === currentMarket) {
      return config.levels[i].quantile
    }
  }

  return 'p10' // No constraint if all preceding levels are fixed or use different markets
}

/**
 * Check if a quantile is allowed for a given level based on cascade constraint.
 */
export function isQuantileAllowed(config: PricingStrategyConfig, levelIndex: number, quantile: PriceQuantile): boolean {
  const minQuantile = getMinQuantileForLevel(config, levelIndex)
  const minIndex = QUANTILE_ORDER.indexOf(minQuantile)
  const quantileIndex = QUANTILE_ORDER.indexOf(quantile)
  return quantileIndex >= minIndex
}

export type UsePricingStrategyStateReturn = {
  config: PricingStrategyConfig
  /** Focused level index for preview panel */
  focusedLevelIndex: number | null
  setFocusedLevelIndex: (index: number | null) => void
  updateMode: (levelId: string, mode: PricingMode) => void
  updateMarket: (levelId: string, market: ForecastMarketPair) => void
  updateQuantile: (levelId: string, quantile: PriceQuantile) => void
  getMinQuantileForLevel: (levelIndex: number) => PriceQuantile
  isQuantileAllowed: (levelIndex: number, quantile: PriceQuantile) => boolean
  /** Reset config when level IDs change */
  syncWithLevels: (levelIds: string[]) => void
}

export function usePricingStrategyState(initialLevelIds: string[]): UsePricingStrategyStateReturn {
  const [config, setConfig] = useState<PricingStrategyConfig>(() => createInitialConfig(initialLevelIds))
  const [focusedLevelIndex, setFocusedLevelIndex] = useState<number | null>(null)

  const updateMode = useCallback((levelId: string, mode: PricingMode) => {
    setConfig((prev) => {
      const levelIndex = prev.levels.findIndex((l) => l.levelId === levelId)
      if (levelIndex === -1) return prev

      const newLevels = [...prev.levels]
      newLevels[levelIndex] = { ...newLevels[levelIndex], mode }

      // When switching to forecast mode, auto-adjust quantile if below minimum
      if (mode === 'forecast') {
        const minQuantile = getMinQuantileForLevel({ levels: newLevels }, levelIndex)
        const currentIndex = QUANTILE_ORDER.indexOf(newLevels[levelIndex].quantile)
        const minIndex = QUANTILE_ORDER.indexOf(minQuantile)
        if (currentIndex < minIndex) {
          newLevels[levelIndex] = { ...newLevels[levelIndex], quantile: minQuantile }
        }
      }

      return { ...prev, levels: newLevels }
    })
  }, [])

  const updateMarket = useCallback((levelId: string, market: ForecastMarketPair) => {
    setConfig((prev) => {
      const levelIndex = prev.levels.findIndex((l) => l.levelId === levelId)
      if (levelIndex === -1) return prev

      const newLevels = [...prev.levels]
      newLevels[levelIndex] = { ...newLevels[levelIndex], market }

      // When market changes, re-check quantile constraints for this and subsequent levels
      // since the constraint only applies within the same market
      for (let i = levelIndex; i < newLevels.length; i++) {
        if (newLevels[i].mode === 'forecast') {
          const minQuantile = getMinQuantileForLevel({ levels: newLevels }, i)
          const currentIndex = QUANTILE_ORDER.indexOf(newLevels[i].quantile)
          const minIndex = QUANTILE_ORDER.indexOf(minQuantile)
          if (currentIndex < minIndex) {
            newLevels[i] = { ...newLevels[i], quantile: minQuantile }
          }
        }
      }

      return { ...prev, levels: newLevels }
    })
  }, [])

  const updateQuantile = useCallback((levelId: string, quantile: PriceQuantile) => {
    setConfig((prev) => {
      const levelIndex = prev.levels.findIndex((l) => l.levelId === levelId)
      if (levelIndex === -1) return prev

      // Validate against cascade constraint
      if (!isQuantileAllowed(prev, levelIndex, quantile)) {
        return prev // Reject invalid quantile
      }

      // Update this level
      const newLevels = [...prev.levels]
      newLevels[levelIndex] = { ...newLevels[levelIndex], quantile }

      // Auto-adjust subsequent levels if their quantile is now below minimum
      for (let i = levelIndex + 1; i < newLevels.length; i++) {
        if (newLevels[i].mode === 'forecast') {
          const minAllowed = getMinQuantileForLevel({ levels: newLevels }, i)
          const currentIndex = QUANTILE_ORDER.indexOf(newLevels[i].quantile)
          const minIndex = QUANTILE_ORDER.indexOf(minAllowed)
          if (currentIndex < minIndex) {
            newLevels[i] = { ...newLevels[i], quantile: minAllowed }
          }
        }
      }

      return { ...prev, levels: newLevels }
    })
  }, [])

  const syncWithLevels = useCallback((levelIds: string[]) => {
    setConfig((prev) => {
      // Keep existing config for levels that still exist
      const existingMap = new Map(prev.levels.map((l) => [l.levelId, l]))

      return {
        levels: levelIds.map((levelId, index) => {
          const existing = existingMap.get(levelId)
          if (existing) return existing

          // New level: inherit from previous or use default
          const prevLevel = index > 0 ? prev.levels[index - 1] : undefined
          return {
            levelId,
            mode: 'fixed' as PricingMode,
            market: (prevLevel?.market ?? 'mfrr') as ForecastMarketPair,
            quantile: QUANTILE_ORDER[Math.min(index, QUANTILE_ORDER.length - 1)],
          }
        }),
      }
    })
  }, [])

  const getMinQuantileForLevelCb = useCallback(
    (levelIndex: number) => getMinQuantileForLevel(config, levelIndex),
    [config],
  )

  const isQuantileAllowedCb = useCallback(
    (levelIndex: number, quantile: PriceQuantile) => isQuantileAllowed(config, levelIndex, quantile),
    [config],
  )

  return useMemo(
    () => ({
      config,
      focusedLevelIndex,
      setFocusedLevelIndex,
      updateMode,
      updateMarket,
      updateQuantile,
      getMinQuantileForLevel: getMinQuantileForLevelCb,
      isQuantileAllowed: isQuantileAllowedCb,
      syncWithLevels,
    }),
    [
      config,
      focusedLevelIndex,
      updateMode,
      updateMarket,
      updateQuantile,
      getMinQuantileForLevelCb,
      isQuantileAllowedCb,
      syncWithLevels,
    ],
  )
}
